import React, { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlarmClock, CalendarClock, IndianRupee, Wallet } from 'lucide-react';
import { getPerformaInvoice } from '../services/api/implementation/impl';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import Loader from '../../shared/components/ui/Loader';
import PaginationComponent from '../../shared/components/PaginationComponent';
import {
  EmptyRow,
  Field,
  FilterBar,
  SegmentedControl,
  StatusPill,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead,
  Tile,
  Tr,
  TileRow
} from '../../shared/components/ui/primitives';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import { money, shortDate } from './SiteWallet/siteWalletUi';
import {
  DownloadPiButton,
  PaymentsMade,
  PiDocuments,
  RecordPaymentForm
} from './PerformaInvoiceParts';

const PAGE_SIZE = 20;

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partially paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' }
];

const STATUS_PILL = {
  pending: { tone: 'warning', label: 'Pending' },
  partial: { tone: 'info', label: 'Partially paid' },
  paid: { tone: 'success', label: 'Paid' }
};

export const filterInput =
  'rounded-control border border-hairline-strong bg-surface-muted px-2 py-1.5 text-sm text-gray-700 outline-none focus:border-brand-700';

const EMPTY = { search: '', project_id: '', vendor_id: '', status: '', from: '', to: '' };

export const PIStatusPill = ({ pi }) => {
  if (pi.payment_status !== 'paid' && pi.is_overdue) {
    return (
      <StatusPill tone="danger" dense>
        Overdue
      </StatusPill>
    );
  }
  const pill = STATUS_PILL[pi.payment_status] ?? STATUS_PILL.pending;
  return (
    <StatusPill tone={pill.tone} dense>
      {pill.label}
    </StatusPill>
  );
};

const NextDue = ({ pi }) => {
  if (pi.payment_status === 'paid') return <span className="text-gray-400">—</span>;
  if (!pi.next_due_date) return <span className="text-gray-400">No terms</span>;
  return (
    <>
      <span className={pi.is_overdue ? 'font-semibold text-danger-fg' : 'text-gray-700'}>
        {shortDate(pi.next_due_date)}
      </span>
      {pi.next_due_term && (
        <div className="text-sm font-normal text-gray-500">{pi.next_due_term}</div>
      )}
    </>
  );
};

const vendorName = (pi) => pi?.vendor?.businessName || pi?.vendor?.user?.name || '—';

const toPaise = (value) => Math.round(Number(value || 0) * 100);

// The server sends status and due; work them out here when it doesn't, so a
// paid PI never shows as Pending.
const withDerived = (pi) => {
  const due = Math.max(toPaise(pi.amount) - toPaise(pi.paidAmount), 0) / 100;
  const paid = pi.isPaid || (toPaise(pi.amount) > 0 && due === 0);
  return {
    ...pi,
    due_amount: pi.due_amount ?? due,
    payment_status:
      pi.payment_status ?? (paid ? 'paid' : toPaise(pi.paidAmount) > 0 ? 'partial' : 'pending')
  };
};

/**
 * Proforma Invoices raised when a vendor's quotation is accepted, and paying
 * them. The next due date comes from the RFQ payment terms: the first term the
 * payments so far don't cover, counted from the PI date.
 */
const PerformaInvoiceList = () => {
  const { projects = [] } = useProjectsList();

  const [filters, setFilters] = useState(EMPTY);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // The PI open in the side modal, and whether it shows details or the payment form.
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('details');
  const [formDirty, setFormDirty] = useState(false);
  const queryClient = useQueryClient();

  // Wait for typing to stop before searching.
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, search: search.trim() })), 350);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => setPage(1), [filters]);

  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const filtered = search || Object.entries(filters).some(([k, v]) => k !== 'search' && v);

  const list = useQuery({
    queryKey: ['payables', page, filters],
    queryFn: () => getPerformaInvoice(page, PAGE_SIZE, filters),
    keepPreviousData: true
  });

  const rows = (list.data?.data ?? []).map(withDerived);
  const meta = list.data?.meta ?? {};
  const summary = meta.summary ?? {};
  const vendors = meta.vendors ?? [];
  const totalPages = meta.total ? Math.ceil(meta.total / PAGE_SIZE) : 1;

  const closeModal = () => {
    setSelected(null);
    setMode('details');
    setFormDirty(false);
  };
  const onDirty = useCallback((dirty) => setFormDirty(dirty), []);

  const afterPayment = () => {
    closeModal();
    list.refetch();
    queryClient.invalidateQueries({ queryKey: ['performaInvoiceHistory'] });
  };

  const terms = [...(selected?.quotation?.rfq?.paymentTerms ?? [])].sort((a, b) => a.id - b.id);
  // The PO carries the accepted lines; before any are marked, show the whole quote.
  const quoteItems = selected?.quotation?.items ?? [];
  const accepted = quoteItems.filter((it) => it.status === 'Accepted');
  const items = accepted.length ? accepted : quoteItems;

  return (
    <div className="mt-[9.5rem] flex flex-col gap-4 px-3 pb-6">
      <Loader isVisible={list.isFetching && !list.data} />

      {list.data?.meta?.summary && (
        <TileRow>
          <Tile
            variant="card"
            label="Total payable"
            hint="Still due on unpaid PIs"
            value={money(summary.total_payable ?? 0)}
            icon={<Wallet size={16} />}
          />
          <Tile
            variant="card"
            label="Due this week"
            hint="Next instalment in 7 days"
            value={money(summary.due_this_week ?? 0)}
            icon={<CalendarClock size={16} />}
            tone="warning"
          />
          <Tile
            variant="card"
            label="Overdue"
            hint={`${summary.overdue_count ?? 0} PI${summary.overdue_count === 1 ? '' : 's'} past due`}
            value={money(summary.overdue ?? 0)}
            icon={<AlarmClock size={16} />}
            tone="danger"
          />
          <Tile
            variant="card"
            label="Paid this month"
            hint={filtered ? 'For these filters' : 'Since the 1st'}
            value={money(summary.paid_this_month ?? 0)}
            icon={<IndianRupee size={16} />}
            tone="success"
          />
        </TileRow>
      )}

      <FilterBar
        end={
          <>
            <Field label="PI date from" size="caption">
              <input
                type="date"
                value={filters.from}
                onChange={set('from')}
                className={filterInput}
              />
            </Field>
            <Field label="To" size="caption">
              <input type="date" value={filters.to} onChange={set('to')} className={filterInput} />
            </Field>
            {filtered && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setFilters(EMPTY);
                }}
                className="mb-2 text-sm font-semibold text-brand-700 hover:underline"
              >
                Clear
              </button>
            )}
          </>
        }
      >
        <Field label="Search" size="caption">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Vendor, PI or PO number"
            className={`${filterInput} w-56`}
          />
        </Field>
        <Field label="Project" size="caption">
          <select value={filters.project_id} onChange={set('project_id')} className={filterInput}>
            <option value="">All projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        {vendors.length > 0 && (
          <Field label="Vendor" size="caption">
            <select value={filters.vendor_id} onChange={set('vendor_id')} className={filterInput}>
              <option value="">All vendors</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <SegmentedControl
          ariaLabel="Payment status"
          size="sm"
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          options={STATUSES}
        />
      </FilterBar>

      <TableCard>
        <Table>
          <THead>
            <tr>
              <Th>PI no.</Th>
              <Th>Vendor</Th>
              <Th>Project</Th>
              <Th>Next due</Th>
              <Th align="right">Amount</Th>
              <Th align="right">Due</Th>
              <Th>Status</Th>
              <Th align="right">Action</Th>
            </tr>
          </THead>
          <TBody>
            {!rows.length && !list.isFetching && (
              <EmptyRow colSpan={8}>
                No proforma invoices{filtered ? ' for these filters' : ' yet'}.
              </EmptyRow>
            )}
            {rows.map((pi) => (
              <Tr key={pi.id} onClick={() => setSelected(pi)}>
                <Td className="whitespace-nowrap font-medium text-brand-700">
                  {pi.pi_number ?? `PI-${pi.id}`}
                </Td>
                <Td>
                  {vendorName(pi)}
                  {pi?.vendor?.businessName && pi?.vendor?.user?.name && (
                    <div className="text-sm text-gray-500">{pi.vendor.user.name}</div>
                  )}
                </Td>
                <Td className="text-gray-600">{pi?.project?.name ?? '—'}</Td>
                <Td className="whitespace-nowrap">
                  <NextDue pi={pi} />
                </Td>
                <Td align="right" numeric className="text-gray-600">
                  {money(pi.amount)}
                </Td>
                <Td align="right" numeric className="font-medium">
                  {money(pi.due_amount)}
                </Td>
                <Td>
                  <PIStatusPill pi={pi} />
                </Td>
                <Td align="right" className="whitespace-nowrap">
                  {pi.payment_status !== 'paid' && (
                    <button
                      type="button"
                      onClick={(e) => {
                        // Straight to the payment form, not the row's details view.
                        e.stopPropagation();
                        setSelected(pi);
                        setMode('pay');
                      }}
                      className="rounded-control border border-brand-700 px-3 py-1 text-sm font-semibold text-brand-700 hover:bg-brand-700 hover:text-white"
                    >
                      Record payment
                    </button>
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </TableCard>

      {meta.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-gray-500">
            {meta.total.toLocaleString('en-IN')} PI{meta.total === 1 ? '' : 's'}
          </span>
          {totalPages > 1 && (
            <div className="flex-1">
              <PaginationComponent
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                isFetching={list.isFetching}
              />
            </div>
          )}
        </div>
      )}

      {selected && (
        <Modal
          isDirty={mode === 'pay' && formDirty}
          confirmOnClose={mode === 'pay' && formDirty}
          width={60}
          onClose={closeModal}
        >
          <div className="flex flex-col gap-5">
            <div className="flex items-start justify-between gap-4 pr-10">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selected.pi_number ?? `PI-${selected.id}`}
                  </h2>
                  <PIStatusPill pi={selected} />
                </div>
                <p className="text-sm font-medium text-gray-800">{vendorName(selected)}</p>
                <p className="text-sm text-gray-500">
                  {[
                    selected?.vendor?.businessName && selected?.vendor?.user?.name,
                    selected?.vendor?.user?.phone_number,
                    selected?.vendor?.user?.email,
                    selected?.vendor?.gst && `GSTIN ${selected.vendor.gst}`
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              {mode === 'details' && (
                <div className="flex shrink-0 items-center gap-2">
                  <DownloadPiButton pi={selected} />
                  {selected.payment_status !== 'paid' && (
                    <Button type="button" onClick={() => setMode('pay')}>
                      Record payment
                    </Button>
                  )}
                </div>
              )}
            </div>

            <dl className="grid grid-cols-3 gap-3 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm">
              <div>
                <dt className="text-gray-500">Amount</dt>
                <dd className="text-base font-semibold">{money(selected.amount)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Paid</dt>
                <dd className="text-base font-semibold text-success-fg">
                  {money(selected.paidAmount)}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Due</dt>
                <dd className="text-base font-semibold">{money(selected.due_amount)}</dd>
              </div>
            </dl>

            {mode === 'details' ? (
              <>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500">Project</dt>
                    <dd className="font-medium">{selected?.project?.name ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">PI date</dt>
                    <dd className="font-medium">{shortDate(selected.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Quotation</dt>
                    <dd className="font-medium">
                      {selected.rfq_id ? (
                        <Link
                          to={`/builder/inventory/orders/requirement/${selected.rfq_id}`}
                          className="text-brand-700 hover:underline"
                        >
                          Quote #{selected.quotation_id}
                        </Link>
                      ) : selected.quotation_id ? (
                        `Quote #${selected.quotation_id}`
                      ) : (
                        '—'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Purchase order</dt>
                    <dd className="font-medium">
                      {selected.po_id ? (
                        <Link
                          to={`/builder/inventory/orders/items/${selected.po_id}`}
                          className="text-brand-700 hover:underline"
                        >
                          {selected.po_number ?? `PO #${selected.po_id}`}
                        </Link>
                      ) : (
                        <span className="text-gray-500">Raised once the advance is paid</span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Next due</dt>
                    <dd className="font-medium">
                      <NextDue pi={selected} />
                    </dd>
                  </div>
                </dl>

                <div>
                  <h3 className="mb-2 text-sm font-semibold text-gray-700">Payment terms</h3>
                  {terms.length > 0 ? (
                    <ul className="list-inside list-disc space-y-1 text-sm text-gray-700">
                      {terms.map((t) => (
                        <li
                          key={t.id}
                          className={t.name === selected.next_due_term ? 'font-semibold' : ''}
                        >
                          {t.name} - {t.percentage}% ·{' '}
                          {money((Number(selected.amount || 0) * Number(t.percentage || 0)) / 100)}
                          {t.duration ? ` · in ${t.duration} days` : ''}
                          {t.name === selected.next_due_term && ' (next)'}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No payment terms on this RFQ.</p>
                  )}
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-semibold text-gray-700">Materials</h3>
                  {items.length > 0 ? (
                    <div className="overflow-x-auto rounded-md border border-gray-200">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left text-gray-500">
                          <tr>
                            <th className="px-3 py-2 font-medium">Item</th>
                            <th className="px-3 py-2 text-right font-medium">Qty</th>
                            <th className="px-3 py-2 text-right font-medium">Rate</th>
                            <th className="px-3 py-2 text-right font-medium">GST</th>
                            <th className="px-3 py-2 text-right font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {items.map((it) => (
                            <tr key={it.id}>
                              <td className="px-3 py-2">
                                {it.item?.item_name ?? `Item #${it.item_id}`}
                                {it.category?.name && (
                                  <div className="text-xs text-gray-500">{it.category.name}</div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {Number(it.quantity ?? 0).toLocaleString('en-IN')}
                                {it.item?.unit ? ` ${it.item.unit}` : ''}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {money(it.perItemPrice)}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                                {it.tax ? `${it.tax}%` : '—'}
                              </td>
                              <td className="px-3 py-2 text-right font-medium tabular-nums">
                                {money(it.totalAmount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No items on this quotation.</p>
                  )}
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-semibold text-gray-700">Payments made</h3>
                  <PaymentsMade piId={selected.id} />
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-semibold text-gray-700">Documents</h3>
                  <PiDocuments documents={selected.documents} />
                </div>
              </>
            ) : (
              <RecordPaymentForm
                pi={selected}
                onDirty={onDirty}
                onBack={() => {
                  setMode('details');
                  setFormDirty(false);
                }}
                onDone={afterPayment}
              />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PerformaInvoiceList;
